import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_API_URL || 'https://api.autoflowapp.net/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('access_token'));

  useEffect(() => {
    if (token) {
      loadUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const loadUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to load user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      // 🔥 FIX: endpoint SIN /api
      const response = await axios.post(`${API_URL}/auth/login`, { email, password });

      const { access_token, refresh_token } = response.data;

      localStorage.setItem('access_token', access_token);
      localStorage.setItem('refresh_token', refresh_token);

      setToken(access_token);

      return true;
    } catch (error) {
      console.error('Login error:', error.response?.data || error.message);
      throw error;
    }
  };

  const logout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
  };

  // Roles
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isAgencyAdmin = user?.role === 'AGENCY_ADMIN';
  const isSales = user?.role === 'SALES';
  const isEditor = user?.role === 'EDITOR';
  const isViewer = user?.role === 'VIEWER';

  const canManageUsers = isSuperAdmin || isAgencyAdmin;
  const canManageVehicles = isSuperAdmin || isAgencyAdmin || isSales || isEditor;
  const hasGlobalAccess = isSuperAdmin;

  const value = {
    user,
    loading,
    token,
    login,
    logout,
    isAuthenticated: !!user,
    isSuperAdmin,
    isAgencyAdmin,
    isSales,
    isEditor,
    isViewer,
    canManageUsers,
    canManageVehicles,
    hasGlobalAccess,
    isAgencyUser: isAgencyAdmin || isSales || isEditor || isViewer
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};